import Navbar from "../assets/Navbar";
import CardDesign from "../components/cardDesign";
import FooterSection from "assets/Footer";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useGetCataloguesWithAllItem from "hook/useGetCataloguesWithAllItem";
import {
  FaChevronLeft,
  FaChevronRight,
  FaSortAmountDown,
  FaSearch,
} from "react-icons/fa";
import TrendingProducts from "components/dashboardComponents/trendingProducts";
import { TbPackageOff } from "react-icons/tb";
import RevealOnScroll from "components/RevealOnScroll";
import BannerShimmer from "components/shimmer/BannerShimmer";
import SubCategoryShimmer from "components/shimmer/SubCategoryShimmer";
import ShimmerCard from "components/shimmer/ShimmerCard";
import NoDataFound from "components/NoDataFound";

const CategoryWiseData = () => {
  const { catalogueId } = useParams();
  const { subGroupedAllData, loading, error } =
    useGetCataloguesWithAllItem(catalogueId);

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [activeSubcategories, setActiveSubcategories] = useState([]);
  const [freeDeliveryOnly, setFreeDeliveryOnly] = useState(false);

  // Scroll Top button
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle subcategory horizontal scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [subGroupedAllData]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -250 : 250,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    setActiveSubcategories([]);
    setSearchTerm("");
    setSortBy("featured");
    setFreeDeliveryOnly(false);
  }, []);

  const toggleSubcategory = (id) => {
    setActiveSubcategories((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const hasAnyItems = subGroupedAllData?.some((c) => c.items?.length > 0);

  return (
    <div className="bg-gray-100 flex flex-col min-h-[100dvh]">
      <Navbar />

      <div className="container mx-auto relative">
        {loading && (
          <div className="space-y-6 p-4">
            <BannerShimmer />
            <SubCategoryShimmer />

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <ShimmerCard key={i} />
              ))}
            </div>
          </div>
        )}

        {!loading && !error && !hasAnyItems && (
          <div className="text-center text-[#265fc9] py-10 my-5">
            <TbPackageOff className="mx-auto w-40 h-40 mb-5 text-backgroundColor animate-pulse" />
            <h3 className="text-lg font-semibold mb-2 mt-5">
              শীঘ্রই নতুন পণ্য যুক্ত করা হবে।
            </h3>
          </div>
        )}

        {!loading &&
          !error &&
          hasAnyItems &&
          subGroupedAllData.map((catalogue) => {
            if (!catalogue.subcatalogues?.length) return null;
            const processedItems = [...(catalogue.items || [])]
              .filter((item) => {
                if (activeSubcategories.length === 0) return true;
                return activeSubcategories.includes(item.subCatalogue);
              })
              .filter((item) => {
                if (!searchTerm) return true;
                const term = searchTerm.toLowerCase();
                return (
                  item.mainTitle?.toLowerCase().includes(term) ||
                  item.subCatalogue_title?.toLowerCase().includes(term)
                );
              })
              .filter((item) => {
                if (!freeDeliveryOnly) return true;
                return item.variants?.some((v) => v.freeVariantDelivery === 1);
              })
              .sort((a, b) => {
                const priceA = a.variants?.[0]?.price || 0;
                const priceB = b.variants?.[0]?.price || 0;

                switch (sortBy) {
                  case "price_low":
                    return priceA - priceB;

                  case "price_high":
                    return priceB - priceA;

                  case "discount_high":
                    const dA = a.variants?.[0]?.discount || 0;
                    const dB = b.variants?.[0]?.discount || 0;
                    return dB - dA;

                  case "name_az":
                    return a.mainTitle.localeCompare(b.mainTitle);

                  default:
                    return 0;
                }
              });
            return (
              <div key={catalogue.catalogue_id} className="mb-5">
                <RevealOnScroll>
                  <section className="relative overflow-hidden shadow-lg">
                    <img
                      src={`https://backend.nuhababy.com/${catalogue.banner_image_path}`}
                      alt={catalogue.title}
                      className="h-24 w-full object-cover sm:h-56 lg:h-64"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/75 via-slate-900/45 to-slate-900/20" />
                    <div className="absolute inset-0 flex items-end p-2 sm:p-6">
                      <div className="text-white">
                        <h1 className="font-extrabold sm:text-3xl">
                          {catalogue.title}
                        </h1>
                      </div>
                    </div>
                  </section>
                </RevealOnScroll>
                <RevealOnScroll delayMs={60}>
                  <div className="px-4 sm:px-6">
                    <section className="mt-4 rounded-lg bg-white px-4 py-2 shadow-sm ring-1 ring-slate-100 sm:px-5 sm:py-5">
                      <div>
                        <h2 className="font-bold text-slate-900 sm:text-xl">
                          SUB CATEGORIES
                        </h2>
                        <p className="text-sm text-slate-600">
                          Choose a subcatalogue to view dedicated items.
                        </p>
                      </div>
                      <div className="relative  my-1">
                        {canScrollLeft && (
                          <button
                            onClick={() => scroll("left")}
                            className="absolute top-1/2 left-0 -translate-y-1/2 bg-white shadow-md p-2 rounded-full z-10 hover:bg-gray-200"
                          >
                            <FaChevronLeft />
                          </button>
                        )}

                        <div
                          ref={scrollRef}
                          className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar scroll-smooth"
                          style={{
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                          }}
                        >
                          <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>

                          {catalogue.subcatalogues.map((sub) => (
                            <div className="flex-shrink-0 xl:w-[200px] md:w-[140px] w-[100px] snap-center">
                              <div className="rounded-[10px] overflow-hidden md:mx-4 mx-2">
                                <Link
                                  key={sub.subCatalogue_id}
                                  to={`/subCategory/${sub.subCatalogue_id}`}
                                >
                                  <div className="md:mb-4 mt-2 sm:mt-4 mb-2 bg-white transition-transform rounded-sm flex items-center justify-center">
                                    <img
                                      src={`https://backend.nuhababy.com/${sub.image_path}`}
                                      alt={sub.title}
                                      className="w-auto h-[50px] md:h-[110px] lg:h-[150px] object-fit"
                                    />
                                  </div>
                                  <div className="pb-2">
                                    <h3 className="text-center lg:text-base md:text-sm text-xs sm:font-bold truncate">
                                      {sub.title}
                                    </h3>
                                  </div>
                                  {/* </div> */}
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>

                        {canScrollRight && (
                          <button
                            onClick={() => scroll("right")}
                            className="absolute top-1/2 right-0 -translate-y-1/2 bg-white shadow-md p-2 rounded-full z-10 hover:bg-gray-200"
                          >
                            <FaChevronRight />
                          </button>
                        )}
                      </div>

                      <div className="mb-3 hidden sm:flex flex-wrap items-center gap-2">
                        {catalogue.subcatalogues.map((subcategory) => {
                          const active = activeSubcategories.includes(
                            subcategory.subCatalogue_id,
                          );

                          return (
                            <button
                              key={subcategory.subCatalogue_id}
                              type="button"
                              onClick={() =>
                                toggleSubcategory(subcategory.subCatalogue_id)
                              }
                              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition sm:text-sm ${
                                active
                                  ? "border-blue-300 bg-blue-50 text-blue-700"
                                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              {subcategory.title}
                              <span className="ml-1 text-[11px] text-slate-500">
                                ({subcategory.total_products})
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <div className="grid gap-3 grid-cols-1 sm:grid-cols-4 items-stretch">
                        <div className="relative sm:col-span-2 h-full">
                          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(event) =>
                              setSearchTerm(event.target.value)
                            }
                            placeholder="Search..."
                            className="w-full h-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:contents">
                          <div className="flex h-full items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-xs sm:text-sm">
                            <FaSortAmountDown className="text-slate-500" />
                            <select
                              value={sortBy}
                              onChange={(event) =>
                                setSortBy(event.target.value)
                              }
                              className="bg-transparent outline-none border-none focus:ring-0"
                            >
                              <option value="featured">Sort By</option>
                              <option value="price_low">Low-High</option>
                              <option value="price_high">High-Low</option>
                              <option value="discount_high">Discount</option>
                              <option value="name_az">A-Z</option>
                            </select>
                          </div>
                          <label className="flex h-full items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-xs sm:text-sm">
                            <input
                              type="checkbox"
                              checked={freeDeliveryOnly}
                              onChange={(event) =>
                                setFreeDeliveryOnly(event.target.checked)
                              }
                              className="h-4 w-4"
                            />
                            Free Delivery
                          </label>
                        </div>
                      </div>
                    </section>
                  </div>
                </RevealOnScroll>

                {/* All items sorted */}
                <div className="mt-5">
                  {processedItems.length > 0 ? (
                    <CardDesign likeData={processedItems} />
                  ) : (
                    <NoDataFound
                      message={
                        searchTerm
                          ? "No search results found"
                          : freeDeliveryOnly
                            ? "No free delivery items available"
                            : "No items found"
                      }
                    />
                  )}
                </div>
              </div>
            );
          })}
      </div>
      <div className="container mx-auto px-4 sm:px-6 md:px-10">
        <TrendingProducts />
      </div>
      <FooterSection />

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 w-8 h-8 bg-red-600 text-white rounded-full shadow-lg hover:bg-red-700 flex items-center justify-center"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default CategoryWiseData;
